import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  sendEmailVerification,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
} from 'firebase/auth';
import { auth } from '../../firebase';
import '../../styles.css';

const Auth = () => {
  const location = useLocation();
  const { user, displayName } = useAuth();
  const [liked, setLiked] = useState([]);
  const initialMode = useMemo(() => (location.pathname.includes('signup') ? 'signup' : 'login'), [location.pathname]);
  const [mode, setMode] = useState(initialMode); // 'login' | 'signup'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    setMode(initialMode);
  }, [initialMode]);

  useEffect(() => {
    // Load current user's likes to show thumbnails
    const loadLikes = async () => {
      try {
        if (!user) { setLiked([]); return; }
        const { db } = await import('../../firebase');
        const { collection, getDocs, query, where } = await import('firebase/firestore');
        const q = query(collection(db, 'imageLikes'), where('userId', '==', user.uid));
        const snap = await getDocs(q);
        const data = snap.docs.map(d => d.data());
        setLiked(data);
      } catch {
        setLiked([]);
      }
    };
    loadLikes();
  }, [user]);

  const switchMode = () => {
    setError('');
    setMessage('');
    setMode((m) => (m === 'login' ? 'signup' : 'login'));
  };

  const handleEmailPassword = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    try {
      if (mode === 'signup') {
        if (password !== confirm) {
          setError('Passwords do not match.');
          return;
        }
        const cred = await createUserWithEmailAndPassword(auth, email, password);
        await sendEmailVerification(cred.user);
        setMessage('Account created. Please check your email for a verification link before logging in.');
      } else {
        const cred = await signInWithEmailAndPassword(auth, email, password);
        if (!cred.user.emailVerified) {
          await sendEmailVerification(cred.user);
          await signOut(auth);
          setError('Email not verified. We re-sent a verification email. Please verify, then log in again.');
        }
      }
    } catch (err) {
      setError('Request failed. Please try again.');
    }
  };

  const handleGoogle = async () => {
    setError('');
    setMessage('');
    try {
      const provider = new GoogleAuthProvider();
      await signInWithPopup(auth, provider);
    } catch (err) {
      setError('Google sign-in failed.');
    }
  };

  return (
    <section className="section contact-section">
      <h2>{user ? `Hello ${displayName}` : (mode === 'login' ? 'Login' : 'Sign Up')}</h2>
      <div className="contact-form" style={{ gap: 12 }}>
        {user && (
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <Link to="/" className="btn-outline">Home</Link>
            <Link to="/gallery" className="btn-outline">Gallery</Link>
            <Link to="/store" className="btn-outline">Store</Link>
          </div>
        )}

        {user && liked.length > 0 && (
          <div style={{ marginTop: 8 }}>
            <h3 style={{ marginBottom: 8 }}>My Likes</h3>
            <div className="image-grid">
              {liked.map((l, idx) => (
                <div className="image-item" key={idx}>
                  <img src={l.imageKey} alt="Liked artwork" className="gallery-image" />
                </div>
              ))}
            </div>
          </div>
        )}
        {!user && (
          <div style={{ display: 'flex', gap: 10 }}>
            <button type="button" className={`btn-outline`} onClick={() => setMode('login')} aria-pressed={mode==='login'}>
              Login
            </button>
            <button type="button" className={`btn-outline`} onClick={() => setMode('signup')} aria-pressed={mode==='signup'}>
              Sign Up
            </button>
          </div>
        )}

        {!user && (
        <form onSubmit={handleEmailPassword}>
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
          </div>
          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
          </div>
          {mode === 'signup' && (
            <div className="form-group">
              <label htmlFor="confirm">Confirm Password</label>
              <input id="confirm" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required />
            </div>
          )}
          <button type="submit" className="btn-submit">{mode === 'login' ? 'Login' : 'Create Account'}</button>
        </form>
        )}

        {!user && (
          <div style={{ marginTop: 8 }}>
            <button type="button" className="btn-outline" onClick={handleGoogle} aria-label="Continue with Google">
              Continue with Google
            </button>
          </div>
        )}

        {message && <p className="success-message">{message}</p>}
        {error && <p className="error-message">{error}</p>}

        {!user && (
          <div style={{ marginTop: 8, fontSize: '0.95rem' }}>
            {mode === 'login' ? (
              <span>Don’t have an account? <button type="button" className="btn-outline" onClick={switchMode}>Sign Up</button></span>
            ) : (
              <span>Already have an account? <button type="button" className="btn-outline" onClick={switchMode}>Login</button></span>
            )}
          </div>
        )}

        <div style={{ marginTop: 8 }}>
          <Link to="/" className="btn-outline">Back to Home</Link>
        </div>
      </div>
    </section>
  );
};

export default Auth;


